import { useLocaleFormatters } from "$root/localization/hooks";
import { type TaskQueueState, TaskQueueStatusIndicator } from "$root/utils";
import type { StylableProps } from "@mdotm/shadcn/legacy/components";
import { type Atom, WithAtom } from "@mdotm/shadcn/legacy/reactivity";
import { Sandwich } from "@mdotm/shadcn/mdotm/layout";

export function SavedAtTaskQueueIndicator({
	taskQueueState$,
	className,
	style,
	onRetry,
}: { taskQueueState$: Atom<TaskQueueState>; onRetry?: () => Promise<void> } & StylableProps) {
	const { formatTime } = useLocaleFormatters();

	return (
		<WithAtom atom$={taskQueueState$}>
			{({ isRunning, lastRunAt }) =>
				/*
				 * Initial state — nothing has run yet, so the indicator has no label to show and the
				 * `Sandwich` would reserve the width of the longest one for an empty row. Collapse the
				 * whole thing instead. Once the first task runs there is always something to report, so
				 * this can only ever fire before the very first run: no appear/disappear churn later.
				 */
				!isRunning && lastRunAt === null ? null : (
					<Sandwich className={className} childrenFill>
						{/* space placeholder */}
						<TaskQueueStatusIndicator
							data-kind="placeholder"
							className="invisible relative"
							style={style}
							taskQueueState$={taskQueueState$}
							labels={{
								idle: `Unable to save changes-`,
								running: `Unable to save changes-`,
								success: () => `Unable to save changes-`,
								error: `Unable to save changes-`,
							}}
							onRetry={onRetry}
						/>
						<TaskQueueStatusIndicator
							data-kind="actual"
							className="absolute"
							style={style}
							taskQueueState$={taskQueueState$}
							labels={{
								running: "Saving...",
								success: (date) => `Saved at ${formatTime(date)}`,
								error: "Unable to save changes",
							}}
							onRetry={onRetry}
						/>
					</Sandwich>
				)
			}
		</WithAtom>
	);
}
